import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { DocintOverrideCreate } from "@/api/admin-api"

import { RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

type BlockedEntityInfo = {
  scopeType: "global" | "pool" | "user"
  scopeId?: string
  period: "daily" | "monthly"
  currentSpend?: number
}

type CreateOverrideDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: DocintOverrideCreate) => void
  isPending: boolean
  /** Pre-populate from a blocked entity (for Unlock flow) */
  initialEntity?: BlockedEntityInfo
}

function getEndOfDay(): string {
  const now = new Date()
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  return end.toISOString()
}

function getEndOfMonth(): string {
  const now = new Date()
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
  return end.toISOString()
}

export function CreateOverrideDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  initialEntity,
}: CreateOverrideDialogProps) {
  const { t } = useTranslation()
  const [scopeType, setScopeType] = useState<"global" | "pool" | "user">("global")
  const [scopeId, setScopeId] = useState("")
  const [overrideType, setOverrideType] = useState<"limit" | "extra_budget" | "bypass">(
    "extra_budget"
  )
  const [period, setPeriod] = useState<"daily" | "monthly">("monthly")
  const [valueSek, setValueSek] = useState("")
  const [reason, setReason] = useState("")
  const [bypassExpiresAt, setBypassExpiresAt] = useState("")

  // Pre-populate from initialEntity when dialog opens
  useEffect(() => {
    if (open && initialEntity) {
      setScopeType(initialEntity.scopeType)
      setScopeId(initialEntity.scopeId || "")
      setPeriod(initialEntity.period)
      // Pre-fill with current spend for quick unlock
      if (initialEntity.currentSpend !== undefined) {
        setValueSek(initialEntity.currentSpend.toFixed(2))
      }
      setOverrideType("extra_budget")
      setReason("")
      setBypassExpiresAt("")
    } else if (!open) {
      // Reset when closing
      setScopeType("global")
      setScopeId("")
      setPeriod("monthly")
      setValueSek("")
      setReason("")
      setBypassExpiresAt("")
      setOverrideType("extra_budget")
    }
  }, [open, initialEntity])

  const handleSubmit = () => {
    const data: DocintOverrideCreate = {
      scope_type: scopeType,
      period: period,
      override_type: overrideType,
      reason,
    }
    if (scopeType !== "global") {
      data.scope_id = scopeId
    }
    if (overrideType !== "bypass") {
      data.value_sek = parseFloat(valueSek)
      // Auto-calculate expiration based on period
      data.expires_at = period === "daily" ? getEndOfDay() : getEndOfMonth()
    } else {
      // Bypass requires explicit expiration
      data.expires_at = new Date(bypassExpiresAt).toISOString()
    }
    onSubmit(data)
  }

  const isValid = reason.trim() && (overrideType === "bypass" ? bypassExpiresAt : valueSek)

  const isUnlockMode = !!initialEntity
  const dialogTitle = isUnlockMode
    ? t("adminDashboard.docint.unlockScope", "Unlock Scope")
    : t("adminDashboard.docint.createOverride", "Create Override")
  const dialogDescription = isUnlockMode
    ? t(
        "adminDashboard.docint.unlockDesc",
        "Grant extra budget, set a new limit, or bypass for the blocked scope"
      )
    : t("adminDashboard.docint.createOverrideDesc", "Grant additional quota or bypass limits")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("adminDashboard.docint.scopeType", "Scope Type")}</Label>
            <Select
              disabled={isUnlockMode}
              value={scopeType}
              onValueChange={(v) => setScopeType(v as typeof scopeType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="global">Global</SelectItem>
                <SelectItem value="pool">Pool</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scopeType === "pool" && (
            <div className="space-y-2">
              <Label>{t("adminDashboard.docint.pool", "Pool")}</Label>
              <Select disabled={isUnlockMode} value={scopeId} onValueChange={setScopeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select pool" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User Pool</SelectItem>
                  <SelectItem value="indexing">Indexing Pool</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {scopeType === "user" && (
            <div className="space-y-2">
              <Label>{t("adminDashboard.docint.userId", "User ID")}</Label>
              <Input
                disabled={isUnlockMode}
                placeholder="User OID"
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("adminDashboard.docint.overrideType", "Override Type")}</Label>
            <Select
              value={overrideType}
              onValueChange={(v) => setOverrideType(v as typeof overrideType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="extra_budget">
                  {t("adminDashboard.docint.extraBudget", "Extra Budget")}
                </SelectItem>
                <SelectItem value="limit">
                  {t("adminDashboard.docint.setLimit", "Set Limit")}
                </SelectItem>
                <SelectItem value="bypass">
                  {t("adminDashboard.docint.bypass", "Bypass (Unlimited)")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {overrideType !== "bypass" && (
            <>
              <div className="space-y-2">
                <Label>{t("adminDashboard.docint.periodLabel", "Period")}</Label>
                <Select
                  disabled={isUnlockMode}
                  value={period}
                  onValueChange={(v) => setPeriod(v as typeof period)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">
                      {t("adminDashboard.docint.periodMonthlyLabel", "Monthly")}
                    </SelectItem>
                    <SelectItem value="daily">
                      {t("adminDashboard.docint.periodDailyLabel", "Daily")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  {period === "monthly"
                    ? t(
                        "adminDashboard.docint.expiresEndOfMonth",
                        "Override expires at end of current month"
                      )
                    : t(
                        "adminDashboard.docint.expiresEndOfDay",
                        "Override expires at end of current day"
                      )}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{t("adminDashboard.docint.valueSek", "Value (SEK)")}</Label>
                <Input
                  min={0}
                  step={0.01}
                  type="number"
                  value={valueSek}
                  onChange={(e) => setValueSek(e.target.value)}
                />
              </div>
            </>
          )}

          {overrideType === "bypass" && (
            <div className="space-y-2">
              <Label>
                {t("adminDashboard.docint.expiresAt", "Expires At")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                type="datetime-local"
                value={bypassExpiresAt}
                onChange={(e) => setBypassExpiresAt(e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                {t(
                  "adminDashboard.docint.bypassExpirationRequired",
                  "Bypass overrides require an expiration date for safety"
                )}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("adminDashboard.docint.reason", "Reason")}</Label>
            <Textarea
              value={reason}
              placeholder={t(
                "adminDashboard.docint.reasonPlaceholder",
                "Why is this override needed?"
              )}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={isPending || !isValid} onClick={handleSubmit}>
            {isPending ? <RefreshCw className="mr-2 size-4 animate-spin" /> : null}
            {t("common.create", "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
